import { isEmpty } from "lodash";
import { getAuthData } from "../storage/LocalStorage";
import { MessageTypeConstants } from "./Message";
import { UserServiceClientApi } from "../api/UserApiServices";
import store from "../../state/configureStore";
import { callBotContextAsyncResult, getUserDetails } from "./initializeBotContext";
import { ADD_NEW_USER_ASSOCIATION } from "../../state/actions/contacts";
import webSocketApiService from "../socket/wsApiServices";

let tempMessages = [];
let tempMessageIntervel = null;
export let queueLength = 0;
export let queue = [];
export let processing = false;
export let retryCount = 1;
export let alreadyProcessedMsgs = [];


// ---------- Queue Helpers ---------- //

export const top = () => (queueLength > 0 ? queue[0] : undefined);

export const pop = () => (queueLength > 0 ? (queueLength--, queue.shift()) : undefined);

export const clear = () => {
  while (top()) pop();
};


export const processAsyncMsg = async () => {
  if (processing) {
    return;
  }
  processing = true;
  while (top()) {
    const message = top();
    for (let i = 0; i < retryCount; ++i) {
      try {
        if (!alreadyProcessedMsgs.includes(message.messageId)) {
          const success = await callBotContextAsyncResult(message);
          if (success) {
            alreadyProcessedMsgs.push(message.messageId);
            break;
          }
        }
      } catch (e) {
        console.log("Error in handling message:", e);
      }
    }
    pop();
  }
  processing = false;
};


export const pushAsyncMsg = (message) => {
  queue[queueLength++] = message;
  let usersAssociation = store.getState().contacts.usersAssociation;

  // fetch & fill the user details for group chat message
  if (message.bot === "im-bot" && !usersAssociation[message.createdBy]) {
    getUserDetails(message.createdBy)
      .then((sender) => {
        store.dispatch({
          type: ADD_NEW_USER_ASSOCIATION,
          data: { userId: sender.userId, userName: sender.userName },
        });
        queueMicrotask(() => {
          processAsyncMsg();
        });
      })
      .catch((err) => console.log("Unable to get user details", err));
  } else {
    queueMicrotask(() => {
      processAsyncMsg();
    });
  }
};

export const shouldReplaceMap = (message, newMessage) => {
  let existingOptions = message.options;
  let newOptions = newMessage.options;
  return !!(
    existingOptions &&
    newOptions &&
    existingOptions.mapId === newOptions.mapId
  );
};


export const REPLACEMENT_ENABLED_FOR = {
  map: { matcher: shouldReplaceMap },
};


export const shouldReplaceMessageByType = (messages, newMessage) => {
  let messageType = newMessage.messageType;
  let type = REPLACEMENT_ENABLED_FOR[messageType];
  if (type) {
    let matcher = type.matcher;
    for (let i = messages.length - 1; i >= 0; i--) {
      let message = messages[i];

      if (!message.archived && matcher && matcher(message, newMessage)) {
        return i;
      }
    }
    return -1;
  }
  return -1;
};


export const placeMessageBasedOnTime = (messages, newMessage) => {
  if (!messages) {
    return;
  }
  if (messages.length === 0) {
    messages.push(newMessage);
    return;
  }

  for (let index = messages.length - 1; index >= 0; index--) {
    if (index !== undefined || index !== null) {
      if (messages[index].messageId === newMessage.messageId) {
        return;
      }
    }
  }

  let newIndex = messages.length;
  let newMessageTime = null;
  for (let index = messages.length - 1; index >= 0; index--) {
    if (index === undefined || index === null) {
      return;
    }
    let temp = messages[index];
    newMessageTime = new Date(newMessage.createdOn).getTime();
    let lastMessageTime = new Date(temp.createdOn).getTime();

    if (temp.self) {
      messages[newIndex] = newMessage;
      break;
    }

    if (newMessageTime < lastMessageTime) {
      messages[index] = newMessage;
      messages[newIndex] = temp;
      newIndex = index;
      newMessage = messages[newIndex];
    } else {
      messages[newIndex] = newMessage;
      break;
    }
  }
};


export const updateConversationMetaData = (timeLine, selectedConversation, conversationId, message) => {
  const currentUserId = getAuthData()?.user.userId;
  if (tempMessages.filter((msg) => msg.messageId === message.messageId).length === 0) {
    tempMessages = [...tempMessages, message];
  }

  if (tempMessages) {
    if (tempMessageIntervel) clearInterval(tempMessageIntervel);
    tempMessageIntervel = setInterval(() => {
      if (
        tempMessages &&
        tempMessages.length &&
        !isEmpty(selectedConversation)
      ) {

        tempMessages = [];
        clearInterval(tempMessageIntervel);
      }
    }, 5000);
  }

  let conversationIndex = getConversationIndex(timeLine, conversationId);
  let conversation = { ...timeLine[conversationIndex] };

  if (["10", "30", "40", "60", "290", "310", "320", "330", "340", "270"].includes(`${message.contentType}`) ||
    message.messageType ===
    MessageTypeConstants.MESSAGE_TYPE_ACCEPT_IGNORE_CONTACT
  ) {
    conversation.lastMessage = createLastMessage(message);
    conversation.modifiedOn = new Date()
  }

  let newTimeLine = [...timeLine];
  if (
    !selectedConversation ||
    conversationId !== selectedConversation.conversationId
  ) {
    if (!message?.self) {
    	if (message?.createdBy !== currentUserId) {
    		// Check if this message already exists in timeline to avoid duplicate counting
    		const existingConv = timeLine.find(c => c.conversationId === conversation.conversationId);
    		const messageExists = existingConv && existingConv.modifiedOn === message.createdOn;
    		
    		if (!messageExists) {
    		conversation.newMessagesCount = conversation.newMessagesCount
    			? conversation.newMessagesCount + 1
    			: 1;
    		conversation.unreadCount = conversation.unreadCount + 1;
    	}
    }
    }
    newTimeLine.splice(conversationIndex, 1);
    newTimeLine.unshift(conversation);
  } else {
    newTimeLine[conversationIndex] = conversation;
  }
  return newTimeLine;
};


export const checkIfConversationExists = (state, conversationId) => {
  let chatLog = state.chatLog;
  return chatLog.get(conversationId) && true;
};


export const getConversationIndex = (timeLine, conversationId) => {
  return timeLine.findIndex(
    (conversation) => conversation.conversationId === conversationId
  );
};

export function getConversationByBotId(state, botId) {
  let timeLine = state.timeLine;
  for (let i = 0; i < timeLine.length; i++) {
    if (timeLine[i].bot && botId === timeLine[i].bot.botId) {
      return timeLine[i];
    }
  }
  return null;
};

export function toggleConversationFavourite({
  conversationId,
  userDomain,
  action,
  userId,
  appType,
  botId,
  channel,
  selectedDomain }) {
  const data = {
    conversationId,
    userDomain,
    action,
    userId,
    platform: "web",
    appType,
    botId,
    channel,
    selectedDomain
  };
  return webSocketApiService.UpdateFavourites(data)
    .then((data) => {
      return data;
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const getConversationById = (state, conversationId) => {
  let timeLine = state.timeLine;
  let index = getConversationIndex(timeLine, conversationId);
  if (index !== -1) {
    return timeLine[index];
  }
  return null;
};


export const createLastMessage = (message) => {
  return {
    content: message.message,
    contentType: message.messageType,
    createdOn: message.createdOn,
  };
}